import { BRAND } from "constants/brand";
import { useState } from "react";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { Ring } from "@uiball/loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as faStarSolid } from "@fortawesome/free-solid-svg-icons";
import { faStar as faStarRegular } from "@fortawesome/free-regular-svg-icons";

import { useVendorPortalReviews } from "hooks/useReviews";
import { formatDate } from "utils/dateFormat";

const TAB_LABELS = {
    all: "الكل",
    product: "المنتجات",
    vendor: "المطعم",
    delivery: "السائق",
};

const Stars = ({ value }) => {
    const filled = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));
    return (
        <span style={{ color: "#f5a623" }}>
            {[1, 2, 3, 4, 5].map((i) => (
                <FontAwesomeIcon key={i} icon={i <= filled ? faStarSolid : faStarRegular} />
            ))}
        </span>
    );
};

const TargetLabel = ({ review }) => {
    if (review.targetType === "product") {
        return <span>منتج: {review.target?.title ?? `#${review.targetId}`}</span>;
    }
    if (review.targetType === "vendor") {
        return <span>المطعم</span>;
    }
    if (review.targetType === "delivery") {
        return <span>السائق: {review.target?.name ?? `#${review.targetId}`}</span>;
    }
    return null;
};

/**
 * Vendor-role "my reviews" page (`/my-reviews`). Single read against
 * the server-scoped /api/reviews/vendor-portal endpoint — the API
 * filters down to:
 *   - the vendor's own profile rating
 *   - reviews on their products
 *   - reviews on the drivers who delivered their orders
 *
 * Vendors only see visible reviews; moderated rows stay invisible
 * even to the vendor (admin-moderation is the only path to hide).
 *
 * The tab bar at the top filters client-side — server already
 * returns the union, the tabs are a UI predicate.
 */
const MyVendorReviews = () => {
    const [page, setPage] = useState(1);
    const [tab, setTab] = useState("all");

    const { reviews, ratingAvg, ratingCount, totalPages, totalCount, loading } =
        useVendorPortalReviews({ page, limit: 20 });

    const filtered = tab === "all" ? reviews : reviews.filter((r) => r.targetType === tab);

    return (
        <div className="items" style={{ padding: "20px" }}>
            <h2 style={{ marginBottom: "20px" }}>تقييمات المطعم</h2>

            <div
                className="surface-card"
                style={{
                    padding: "20px",
                    marginBottom: "20px",
                    display: "flex",
                    gap: "40px",
                    alignItems: "center",
                    flexWrap: "wrap",
                }}
            >
                <div>
                    <div style={{ color: "#777", fontSize: "13px" }}>متوسط التقييم</div>
                    <div style={{ fontSize: "32px", fontWeight: 700, color: BRAND }}>
                        {ratingAvg !== null ? `★ ${Number(ratingAvg).toFixed(2)}` : "—"}
                    </div>
                </div>
                <div>
                    <div style={{ color: "#777", fontSize: "13px" }}>
                        عدد التقييمات (إجمالي للمطعم)
                    </div>
                    <div style={{ fontSize: "32px", fontWeight: 700 }}>{ratingCount}</div>
                </div>
                <div>
                    <div style={{ color: "#777", fontSize: "13px" }}>إجمالي التقييمات المعروضة</div>
                    <div style={{ fontSize: "32px", fontWeight: 700 }}>{totalCount}</div>
                </div>
            </div>

            <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
                {Object.keys(TAB_LABELS).map((key) => (
                    <button
                        key={key}
                        type="button"
                        className={`btn btn-sm ${
                            tab === key ? "btn-primary" : "btn-outline-secondary"
                        }`}
                        onClick={() => setTab(key)}
                    >
                        {TAB_LABELS[key]}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    {filtered.length === 0 ? (
                        <div
                            className="surface-card surface-card--dashed"
                            style={{
                                padding: "60px 20px",
                                textAlign: "center",
                            }}
                        >
                            {totalCount === 0
                                ? "لم يقم الزبائن بتقييمك بعد. ابدأ بتقديم خدمة ممتازة ⭐"
                                : "لا توجد تقييمات في هذا التصنيف"}
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {filtered.map((review) => (
                                <div
                                    key={review.id}
                                    className="surface-card"
                                    style={{ padding: "16px" }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "8px",
                                            flexWrap: "wrap",
                                            gap: "8px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}
                                        >
                                            <Stars value={review.rating} />
                                            <strong>{review.user?.displayName ?? "زبون"}</strong>
                                            <span style={{ color: "#999" }}>·</span>
                                            <TargetLabel review={review} />
                                        </div>
                                        <span style={{ color: "#777", fontSize: "13px" }}>
                                            {formatDate(review.createdAt)}
                                        </span>
                                    </div>
                                    {review.comment ? (
                                        <p
                                            style={{
                                                marginTop: "8px",
                                                marginBottom: 0,
                                                color: "#444",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {review.comment}
                                        </p>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}

                    {totalPages > 1 ? (
                        <Stack
                            spacing={2}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                padding: "20px",
                            }}
                        >
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                            />
                        </Stack>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default MyVendorReviews;
